/** DarkSelect — dark-theme dropdown replacing native <select>. */
import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export default function DarkSelect({ value, onChange, options = [], compact = false, testid, placeholder = "" }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) setHi(Math.max(0, options.findIndex((o) => o.value === value)));
  }, [open, options, value]);

  const current = options.find((o) => o.value === value);

  const pick = (o) => {
    onChange(o.value);
    setOpen(false);
  };

  const onKeys = (e) => {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (options[hi]) pick(options[hi]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testid ? `${testid}-trigger` : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeys}
        className={`flex w-full items-center justify-between gap-2 rounded-xl border border-zinc-700 bg-zinc-900 text-left text-zinc-100 transition-colors hover:border-zinc-500 focus:outline-none focus-visible:border-emerald-500 ${
          compact ? "px-2.5 py-1.5 text-xs" : "px-3 py-2.5 text-sm"
        }`}
      >
        <span className={`truncate ${!current || current.value === "" ? "text-zinc-500" : ""}`}>
          {current && current.value !== "" ? current.label : (placeholder || current?.label || "")}
        </span>
        <ChevronDown size={compact ? 12 : 14} className="shrink-0 text-zinc-500" aria-hidden />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-40 mt-1 max-h-64 min-w-full overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 py-1 shadow-xl shadow-black/40"
        >
          {options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
              onClick={() => pick(o)}
              onMouseEnter={() => setHi(i)}
              className={`flex w-full items-center justify-between gap-3 whitespace-nowrap px-3 py-2 text-left ${
                compact ? "text-xs" : "text-sm"
              } ${i === hi ? "bg-zinc-800" : ""} ${
                o.value === value ? "text-emerald-400" : "text-zinc-200"
              }`}
            >
              {o.label}
              {o.value === value && <Check size={13} aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
